import type { DesignFilm } from '../lib/api'
import { onMenuPage } from './pages'
import { pageIds } from './types'
import type { Fault, LayoutDocument, LayoutElement, LayoutMedia, MediaItem, MediaKind, MediaSlot } from './types'
import { t, type StaffKey, type StaffLanguage } from '../lib/staffText'

/**
 * The editor's side of a playlist: the rules the server keeps
 * (internal/layout/media.go), said before Publish, and the small conversions the
 * panel needs. A second copy of the rules, which is a cost worth naming — the
 * server is the authority, and this exists so the button is disabled with a reason
 * rather than refused.
 *
 * The sentences are the editor's problems() list, in the reader's language.
 */

export const MAX_PLAYLIST_ITEMS = 30
export const MAX_MEDIA_SLOTS = 12
/** layout.MinItemSeconds and MaxItemSeconds. */
export const MIN_ITEM_SECONDS = 3
export const MAX_ITEM_SECONDS = 3600
/** A picture added to a playlist shows this long until changed (playlist.DEFAULT_PICTURE_SECONDS). */
export const NEW_PICTURE_SECONDS = 10

/** A film's length as the item's whole seconds, rounded up so the film ends first; none when unknown. */
export function filmSeconds(duration: number | undefined): number | undefined {
  if (duration === undefined || !Number.isFinite(duration) || duration <= 0) return undefined
  return Math.min(MAX_ITEM_SECONDS, Math.max(MIN_ITEM_SECONDS, Math.ceil(duration)))
}

/** A new playlist item: a picture for ten seconds, a film for its own length when known. */
export function newMediaItem(assetId: string, kind: MediaKind, duration?: number): MediaItem {
  const seconds = kind === 'image' ? NEW_PICTURE_SECONDS : filmSeconds(duration)
  return seconds === undefined ? { assetId, kind } : { assetId, kind, seconds }
}

/** A film with its sound on or off; off is left out of the document, as muted is the default. */
export function withSound(item: MediaItem, on: boolean): MediaItem {
  const { sound: _, ...rest } = item
  return on && item.kind === 'video' ? { ...rest, sound: true } : rest
}

/** ISO weekdays, Monday first, as the week is written in Azerbaijan. */
export const DAYS: readonly number[] = [1, 2, 3, 4, 5, 6, 7]

const dayName = (day: number, lang: StaffLanguage) => t(`designs.media.dayLong.${day}` as StaffKey, lang)

/**
 * "HH:MM" as minutes since midnight, the way clockMinutes in media.go reads it:
 * two digits each, and 24:00 as the end of the day. A plain string on both sides,
 * never a Date, because the time is the branch's wall clock and a Date would pass
 * it through the zone of whichever browser the designer happens to be using.
 */
export function clockMinutes(s: string): number | null {
  const m = /^(\d\d):(\d\d)$/.exec(s)
  if (!m) return null
  const hours = Number(m[1])
  const minutes = Number(m[2])
  if (minutes > 59 || hours > 24 || (hours === 24 && minutes !== 0)) return null
  return hours * 60 + minutes
}

/** A time box cannot say 24:00, so 00:00 (or nothing) as an end means midnight. */
export function endForStorage(input: string): string {
  return input === '' || input === '00:00' ? '24:00' : input
}

export function endForInput(stored: string): string {
  return stored === '24:00' ? '00:00' : stored
}

export function mediaKindOf(contentType: string): MediaKind | null {
  if (contentType.startsWith('image/')) return 'image'
  if (contentType.startsWith('video/')) return 'video'
  return null
}

export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return [...list]
  const out = [...list]
  const [moved] = out.splice(from, 1)
  out.splice(to, 0, moved as T)
  return out
}

/** A kiosk's idle playlist: a media element on the welcome screen only, which it covers while asleep. */
export function isIdlePlaylist(el: LayoutElement): boolean {
  return el.kind === 'media' && el.screens?.length === 1 && el.screens[0] === 'attract'
}

/** A new time: weekdays at lunch, with nothing in it yet. */
export function newSlot(): MediaSlot {
  return { days: [1, 2, 3, 4, 5], from: '12:00', to: '14:00', items: [] }
}

/** Whether two boxes overlap; boxes that share an edge do not. */
function covers(m: LayoutElement, c: LayoutElement): boolean {
  return m.x < c.x + c.w && c.x < m.x + m.w && m.y < c.y + c.h && c.y < m.y + m.h
}

/** What the server would refuse about the playlists in a design, in the editor's words. */
export function mediaProblems(doc: LayoutDocument, lang: StaffLanguage = 'en', withEmpty = true): string[] {
  return mediaFaults(doc, lang, withEmpty).map((f) => f.text)
}

export function mediaFaults(doc: LayoutDocument, lang: StaffLanguage = 'en', withEmpty = true): Fault[] {
  const all: Fault[] = []
  for (const el of doc.elements) {
    if (el.kind !== 'media') continue
    const found: string[] = []
    const media = el.media ?? { items: [] }
    found.push(...kioskProblems(doc, el, lang))
    if (media.source === 'playlist') {
      all.push(...found.map((text) => ({ text, ids: [el.id] })))
      continue
    }
    const schedule = media.schedule ?? []
    const lists = [media.items, ...schedule.map((s) => s.items)]
    const total = lists.reduce((n, items) => n + items.length, 0)
    if (total === 0 && withEmpty) found.push(t('designs.fault.mediaEmpty', lang))
    if (lists.some((items) => items.length > MAX_PLAYLIST_ITEMS)) {
      found.push(t('designs.fault.mediaTooMany', lang, { max: MAX_PLAYLIST_ITEMS }))
    }
    // The panel's number box has the same bounds, but a box's min and max are a
    // suggestion the keyboard can ignore.
    const wrongLength = lists
      .flat()
      .find((item) => item.seconds && (item.seconds < MIN_ITEM_SECONDS || item.seconds > MAX_ITEM_SECONDS))
    if (wrongLength) {
      found.push(t('designs.fault.mediaSeconds', lang, { seconds: wrongLength.seconds ?? 0, min: MIN_ITEM_SECONDS, max: MAX_ITEM_SECONDS }))
    }
    if (schedule.length > MAX_MEDIA_SLOTS) found.push(t('designs.fault.mediaSlots', lang, { max: MAX_MEDIA_SLOTS }))
    schedule.forEach((slot, i) => {
      const from = clockMinutes(slot.from)
      const to = clockMinutes(slot.to)
      if (slot.days.length === 0) found.push(t('designs.fault.slotNoDays', lang, { slot: i + 1 }))
      // As slotProblems says it: a box left empty is not a time, which is a
      // different fault from a time that ends before it starts, and has a
      // different fix.
      if (from === null) found.push(t('designs.fault.slotNotTime', lang, { slot: i + 1, value: JSON.stringify(slot.from) }))
      if (to === null) found.push(t('designs.fault.slotNotTime', lang, { slot: i + 1, value: JSON.stringify(slot.to) }))
      if (from !== null && to !== null && from >= to) found.push(t('designs.fault.slotBackwards', lang, { slot: i + 1 }))
    })
    for (let i = 0; i < schedule.length; i++) {
      for (let j = i + 1; j < schedule.length; j++) {
        const a = schedule[i]!
        const b = schedule[j]!
        const [af, at, bf, bt] = [a.from, a.to, b.from, b.to].map(clockMinutes)
        if (af == null || at == null || bf == null || bt == null || !(af < bt && bf < at)) continue
        const day = a.days.find((d) => b.days.includes(d))
        if (day !== undefined) found.push(t('designs.fault.slotsOverlap', lang, { a: i + 1, b: j + 1, day: dayName(day, lang) }))
      }
    }
    all.push(...found.map((text) => ({ text, ids: [el.id] })))
  }
  return all
}

/** Where a playlist stands over something: said, never refused (the designer may mean it). */
export function mediaWarnings(doc: LayoutDocument, lang: StaffLanguage = 'en'): Fault[] {
  const all: Fault[] = []
  const calls = doc.elements.filter((el) => el.kind === 'calls')
  const pages = pageIds(doc)
  for (const el of doc.elements) {
    if (el.kind !== 'media' || el.hidden) continue
    const of = (key: StaffKey) => all.push({ text: t(key, lang), ids: [el.id] })
    if (calls.some((c) => !c.hidden && covers(el, c))) of('designs.fault.mediaOverCalls')
    if (doc.kind !== 'kiosk' || el.screens?.length !== 1 || el.screens[0] !== 'choosing') continue
    if (el.y + el.h > doc.canvas.height - KIOSK_CHROME_HEIGHT) of('designs.fault.bannerChrome')
    const pressed = doc.elements.some(
      (b) =>
        (b.kind === 'services' || b.kind === 'button' || b.kind === 'languages') &&
        !b.hidden &&
        covers(el, b) &&
        pages.some((p) => onMenuPage(el, p) && onMenuPage(b, p)),
    )
    if (pressed) of('designs.fault.bannerButtons')
  }
  return all
}

/** An own-items playlist with nothing in it: a draft may hold it, Publish refuses it. */
export function emptyPlaylist(el: LayoutElement): boolean {
  if (el.kind !== 'media' || el.media?.source === 'playlist') return false
  const media = el.media ?? { items: [] }
  return [media.items, ...(media.schedule ?? []).map((s) => s.items)].every((items) => items.length === 0)
}

/** layout.KioskChromeHeight: the strip at a kiosk's foot holding its own Back and booking buttons. */
export const KIOSK_CHROME_HEIGHT = 160

/** The server's kiosk rule (kioskMediaProblems): the welcome screen or a menu banner. */
function kioskProblems(doc: LayoutDocument, el: LayoutElement, lang: StaffLanguage): string[] {
  if (doc.kind !== 'kiosk') return []
  const screens = el.screens ?? []
  if (screens.length !== 1 || (screens[0] !== 'attract' && screens[0] !== 'choosing')) {
    return [t('designs.fault.kioskMediaPlace', lang)]
  }
  return []
}

/** Which list of a playlist something goes into: its own, or the list of time n (0-based). */
export type MediaTarget = 'own' | number

/**
 * A document with one more item at the end of a list of one playlist, found by the
 * element's id rather than by whatever the inspector happens to be showing.
 *
 * An upload can take a minute, and by the time it finishes the designer may have
 * selected another playlist, removed this one, or removed the time it was meant
 * for. Adding it by id to the document as it is then puts it where it was asked
 * for or nowhere. Nowhere returns the same document, which the caller reads as
 * "nothing to save"; the file is still uploaded and is in the list to choose.
 */
export function withMediaItem(
  doc: LayoutDocument,
  elementId: string,
  target: MediaTarget,
  item: MediaItem,
): LayoutDocument {
  const el = doc.elements.find((e) => e.id === elementId)
  if (!el || el.kind !== 'media') return doc
  const media = el.media ?? { items: [] }
  let next: LayoutMedia
  if (target === 'own') {
    next = { ...media, items: [...media.items, item] }
  } else {
    const slots = media.schedule ?? []
    if (!slots[target]) return doc
    next = { ...media, schedule: slots.map((s, j) => (j === target ? { ...s, items: [...s.items, item] } : s)) }
  }
  return { ...doc, elements: doc.elements.map((e) => (e.id === elementId ? { ...e, media: next } : e)) }
}

/**
 * A document whose picture element draws a newly uploaded file, found by the
 * element's id in the document as it is when the upload finishes.
 *
 * The same reason as withMediaItem: while the file was on its way the designer may
 * have selected something else, moved things, or removed the picture, and writing
 * back the element as it was when the upload began would undo all of that. A
 * picture that has gone, or an id that is not a picture, returns the same document.
 */
export function withPictureAsset(doc: LayoutDocument, elementId: string, assetId: string): LayoutDocument {
  const el = doc.elements.find((e) => e.id === elementId)
  if (!el || (el.kind !== 'image' && el.kind !== 'button' && el.kind !== 'logo')) return doc
  // A retired icon goes with the edit, so the button's data says what it draws.
  return {
    ...doc,
    elements: doc.elements.map((e) => (e.id === elementId ? { ...e, assetId, ...(e.kind === 'button' ? { icon: undefined } : {}) } : e)),
  }
}

/** The films a person may delete now (a new film only if they uploaded it), somebody
 *  else's new films they must wait for, and whether every film is in use. */
export function filmChoices(films: readonly DesignFilm[]): { deletable: DesignFilm[]; waiting: DesignFilm[]; allInUse: boolean } {
  return {
    deletable: films.filter((f) => !f.live && (!f.new || f.yours)),
    waiting: films.filter((f) => !f.live && f.new && !f.yours),
    allInUse: films.every((f) => f.live),
  }
}
